// SLD for the Query/Proximity selection highlight — a WMS overlay rendered
// server-side with a CQL_FILTER (see useCesiumEffects).

const DEFAULT_FILL = '#00FFFF';
const DEFAULT_POLYGON_STROKE = '#00B7C3';
const DEFAULT_FILL_OPACITY = 0.35;

interface HighlightPalette {
  fill: string;
  polygonStroke: string;
  fillOpacity: number;
}

// antd appends the alpha byte only when the picker drops below 100%; a fully
// opaque pick keeps the default translucency so data stays visible beneath.
export const parseHighlightColor = (color?: string | null): HighlightPalette => {
  const match = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(color?.trim() ?? '');
  if (!match) return { fill: DEFAULT_FILL, polygonStroke: DEFAULT_POLYGON_STROKE, fillOpacity: DEFAULT_FILL_OPACITY };

  const hex = `#${match[1]}`;
  const fillOpacity = match[2] ? Number((parseInt(match[2], 16) / 255).toFixed(2)) : DEFAULT_FILL_OPACITY;
  return { fill: hex, polygonStroke: hex, fillOpacity };
};

const polygonSymbolizer = ({ fill, polygonStroke, fillOpacity }: HighlightPalette) =>
  `<PolygonSymbolizer><Fill><CssParameter name="fill">${fill}</CssParameter><CssParameter name="fill-opacity">${fillOpacity}</CssParameter></Fill>` +
  `<Stroke><CssParameter name="stroke">${polygonStroke}</CssParameter><CssParameter name="stroke-width">3</CssParameter></Stroke></PolygonSymbolizer>`;

const lineSymbolizer = ({ fill }: HighlightPalette) =>
  `<LineSymbolizer><Stroke><CssParameter name="stroke">${fill}</CssParameter><CssParameter name="stroke-width">5</CssParameter></Stroke></LineSymbolizer>`;

// A highlighted point: a circle sized to the marker it covers, ringed in white or black only when its colour is close to the feature's.
export interface HighlightStyle {
  color: string;
  size: number;
  ringColor: string;
}

export const HIGHLIGHT_POINT_OPACITY = 0.55;
const DEFAULT_POINT_SIZE = 14;
export const HIGHLIGHT_POINT_RING_WIDTH = 2;
const CONTRAST_LIGHT = '#FFFFFF';
const CONTRAST_DARK = '#000000';
// CIELAB distance (ΔE76) under which two colours read as the same.
const COLOR_MATCH_DELTA_E = 30;
const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})(?:[0-9a-f]{2})?$/i;

const toRgb = (css: string): [number, number, number] | null => {
  const hex = HEX_COLOR.exec(css.trim())?.[1];
  if (!hex) return null;
  const full = hex.length === 3 ? [...hex].map((digit) => digit + digit).join('') : hex;
  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
};

const toLinear = (channel: number) => {
  const value = channel / 255;
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
};

const labPivot = (ratio: number) => (ratio > 0.008856 ? Math.cbrt(ratio) : 7.787 * ratio + 16 / 116);

const toLab = ([red, green, blue]: [number, number, number]) => {
  const [r, g, b] = [toLinear(red), toLinear(green), toLinear(blue)];
  const x = labPivot((0.4124 * r + 0.3576 * g + 0.1805 * b) / 0.95047);
  const y = labPivot(0.2126 * r + 0.7152 * g + 0.0722 * b);
  const z = labPivot((0.0193 * r + 0.1192 * g + 0.9505 * b) / 1.08883);
  return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
};

const isCloseTo = (a: [number, number, number], b: [number, number, number]) => {
  const [l1, a1, b1] = toLab(a);
  const [l2, a2, b2] = toLab(b);
  return Math.hypot(l1 - l2, a1 - a2, b1 - b2) < COLOR_MATCH_DELTA_E;
};

// Black on a light colour, white on a dark one.
const contrastWith = ([red, green, blue]: [number, number, number]) =>
  (0.299 * red + 0.587 * green + 0.114 * blue) / 255 > 0.5 ? CONTRAST_DARK : CONTRAST_LIGHT;

// `colors` and `pointSize` are how the feature is drawn; `extraUnderColors` are any highlight beneath it. A non-hex colour never matches.
export const resolveHighlightStyle = (
  color: string | null | undefined,
  { colors, pointSize }: { colors: string[]; pointSize: number | null },
  extraUnderColors: string[] = [],
): HighlightStyle => {
  const css = color || DEFAULT_FILL;
  const rgb = toRgb(css);
  const matches =
    !!rgb &&
    [...colors, ...extraUnderColors].some((under) => {
      const underRgb = toRgb(under);
      return !!underRgb && isCloseTo(rgb, underRgb);
    });
  return { color: css, size: pointSize ?? DEFAULT_POINT_SIZE, ringColor: matches && rgb ? contrastWith(rgb) : css };
};

// Without a style (the query/proximity tools) the point is a fixed size with a white ring.
const pointSymbolizer = ({ fill }: HighlightPalette, point?: HighlightStyle) => {
  const opacity = point ? (name: string) => `<CssParameter name="${name}">${HIGHLIGHT_POINT_OPACITY}</CssParameter>` : () => '';
  return (
    `<PointSymbolizer><Graphic><Mark><WellKnownName>circle</WellKnownName>` +
    `<Fill><CssParameter name="fill">${fill}</CssParameter>${opacity('fill-opacity')}</Fill>` +
    `<Stroke><CssParameter name="stroke">${point ? parseHighlightColor(point.ringColor).fill : CONTRAST_LIGHT}</CssParameter>` +
    `<CssParameter name="stroke-width">${HIGHLIGHT_POINT_RING_WIDTH}</CssParameter>${opacity('stroke-opacity')}</Stroke></Mark>` +
    `<Size>${point?.size ?? DEFAULT_POINT_SIZE}</Size></Graphic></PointSymbolizer>`
  );
};

// layerType is only usable when it names a real geometry. KML and polygon
// shapefiles report 'Geometry' (an untyped geom column), others 'vector' or null.
const isGeometricType = (layerType?: string): layerType is string =>
  layerType === 'Point' || layerType === 'MultiPoint' || !!layerType?.includes('Line') || !!layerType?.includes('Polygon');

const geometryTypeIs = (...types: string[]) => {
  const equals = types
    .map(
      (type) =>
        `<ogc:PropertyIsEqualTo><ogc:Function name="geometryType"><ogc:PropertyName>geom</ogc:PropertyName></ogc:Function>` +
        `<ogc:Literal>${type}</ogc:Literal></ogc:PropertyIsEqualTo>`,
    )
    .join('');
  return `<ogc:Filter><ogc:Or>${equals}</ogc:Or></ogc:Filter>`;
};

// GeoServer paints every symbolizer in a rule, putting a marker on the centroid
// of line/area features — so each rule must resolve to exactly one geometry.
const rulesFor = (layerType: string | undefined, palette: HighlightPalette, point?: HighlightStyle): string => {
  if (!isGeometricType(layerType)) {
    return (
      `<Rule>${geometryTypeIs('Polygon', 'MultiPolygon')}${polygonSymbolizer(palette)}</Rule>` +
      `<Rule>${geometryTypeIs('LineString', 'MultiLineString')}${lineSymbolizer(palette)}</Rule>` +
      `<Rule>${geometryTypeIs('Point', 'MultiPoint')}${pointSymbolizer(palette, point)}</Rule>`
    );
  }
  if (layerType.includes('Line')) return `<Rule>${lineSymbolizer(palette)}</Rule>`;
  if (layerType.includes('Polygon')) return `<Rule>${polygonSymbolizer(palette)}</Rule>`;
  return `<Rule>${pointSymbolizer(palette, point)}</Rule>`;
};

export const buildHighlightSld = (layer: string, layerType?: string, color?: string | null, point?: HighlightStyle): string =>
  `<StyledLayerDescriptor version="1.0.0" xmlns="http://www.opengis.net/sld" xmlns:ogc="http://www.opengis.net/ogc">` +
  `<NamedLayer><Name>${layer}</Name><UserStyle><FeatureTypeStyle>` +
  rulesFor(layerType, parseHighlightColor(color), point) +
  `</FeatureTypeStyle></UserStyle></NamedLayer></StyledLayerDescriptor>`;
